import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Dialog, DialogActions, DialogContent, Grid, TextField } from '@mui/material';
import { OrganizationInfoDto, RepositoryDto, Roles } from '../../models/APIModels';
import { ApiHelper } from '../../common/ApiHelper';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { userInfoSelector } from '../../store/slices/userInfo';
import useAppUtils from '../../Utils/useAppUtils';
import Button from '../Button';
import Loader from '../Loader';
import { StyledTextarea } from '../Inputs/CustomTextarea';
import DialogHeader from './DialogHeader';
import UserManagement from '../UserManagement';
import AutoComplete from '../AutoComplete/AutoCompleteSelect';
import Progress from '../Loaders/Progress';

interface IProps {
  open: boolean;
  close: (reloadList: boolean) => void;
  repositoryItem: RepositoryDto;
  organizations: OrganizationInfoDto[];
}

const RepositoryModal = ({ open, close, repositoryItem, organizations }: IProps) => {
  const { userInfo } = useSelector(userInfoSelector);
  const { user, isAuthenticated } = useAuth0();
  const { alert } = useAppUtils();
  const [usersArr, setUsersArr] = useState([]);
  const [repDetails, setRepDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingDisable, setIsEditingDisable] = useState(false);
  const [isExisting, setIsExisting] = useState(repositoryItem && repositoryItem && repositoryItem.repositoryId ? true : false);
  const [selectedOrganization, setSelectedOrganization] = useState('');

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<RepositoryDto>({
    defaultValues: useMemo(() => {
      // console.log('repositoryItem form changed', repositoryItem);
      return repositoryItem;
    }, [repositoryItem]),
  });

  useEffect(() => {
    if (isAuthenticated && repositoryItem) {
      if(repositoryItem.permissions && !repositoryItem.permissions.includes("Write")){
        setIsEditingDisable(true);
      }
      const organizationItem = organizations.find((o) => o.organizationId === repositoryItem.ownerId);
      if (organizationItem) {
        setSelectedOrganization(organizationItem);
      }
    }
  }, []);

  const updateAccessInfo = (userArr) => {
    setUsersArr(userArr);
    // console.log('userArr', userArr);
    setValue('repositoryAccessRights', userArr);
  };

  const handleOrganizationChange = (organization) => {
    setSelectedOrganization(organization);
    setValue('ownerId', organization.organizationId);
  };
  const onSubmit = async (data: RepositoryDto) => {
    setIsLoading(true);
    // console.log('data', data);
    try {
      const response = await ApiHelper.UpsertRepository(data);
      setIsLoading(false);
      if (response) {
        close(true);
        alert(data.id ? 'Repository updated.' : 'Repository created.');
      }
    } catch (e) {
      console.error('Unable to save repository', e);
      const msg = e && e.response && e.response.data ? e.response.data :'Unable to save repository'
      alert(msg, 'error');
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth={true}>
      {isLoading && <Progress  progressLabel='Please Wait ...'/>}
      <DialogHeader onClose={() => close(false)}>
        <div>{isExisting ? 'Update Repository' : 'Create Repository'}</div>
      </DialogHeader>
      <DialogContent dividers>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Repository Name"
              variant="outlined"
              size="small"
              disabled={false}
              placeholder="Enter Repository Name"
              {...register('repositoryName', { required: true })}
              error={Boolean(errors['repositoryName'])}
              // required
              inputProps={{
                maxLength: 250,
              }}
              helperText={errors['repositoryName'] ? 'Repository Name is required' : ''}
            ></TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              size="small"
              disabled={false}
              placeholder="Description"
              {...register('repositoryDescription', { required: true })}
              error={Boolean(errors['repositoryDescription'])}
              // required
              inputProps={{
                maxLength: 250,
              }}
              multiline
              minRows={5}
              maxRows={8}
              helperText={errors['repositoryDescription'] ? 'Description is required' : ''}
            ></TextField>
          </Grid>
          <Grid item xs={12}>
            <AutoComplete
              id="select-organization"
              items={organizations}
              itemText="organizationName"
              itemKey="organizationId"
              labelText="Organization"
              placeHolderText="Select Organization"
              multiSelect={false}
              onChange={handleOrganizationChange}
              defaultValue={selectedOrganization}
            />
          </Grid>
        </Grid>
        <UserManagement
          users={getValues('repositoryAccessRights')}
          isEditingDisable={isEditingDisable}
          updateAccessInfo={updateAccessInfo}
          title="Repository Users"
          entity="Repository"
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={isLoading} rounded onClick={() => close(false)}>
          Cancel
        </Button>
        <Button variant="contained" rounded onClick={handleSubmit(onSubmit)}>
          {isExisting ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RepositoryModal;
